import { setInlineStyles } from "util/style-utils";
import Constants from "const/constants";
import { inject, observer } from "mobx-react";
import {
    CustomDropTarget,
    onTimelineLayerHover,
    onTimelineLayerDrop,
    timelineLayerCanDrop,
    allowedLayerDragSources,
    layerHeight
} from "pages/editor/lib/timeline";
import PropTypes from "prop-types";
import * as React from "react";
import styled from "styled-components";

const spec = {
    drop: onTimelineLayerDrop,
    hover: onTimelineLayerHover,
    canDrop: timelineLayerCanDrop
};

const collect = (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
    canDrop: monitor.canDrop()
});

export const getLayerBorderColor = (
    isOver,
    canDrop,
    validColor,
    invalidColor
) => {
    if (isOver && canDrop) {
        return validColor;
    }
    if (isOver && !canDrop) {
        return invalidColor;
    }
    return `transparent`;
};

const LayerContainer = setInlineStyles(
    styled.div,
    ({ theme, isOver, canDrop }) => ({
        border: `1px solid ${getLayerBorderColor(
            isOver,
            canDrop,
            theme.colors.secondaryLighter,
            theme.colors.primary
        )}`
    })
)`
	display: flex;
	align-items: center;
	position: relative;
	transition: border-color .1s linear;
	box-sizing: border-box;
	border-radius: 3px;
	height: ${({ layer }) => layerHeight(layer)}px;
	margin-bottom: ${({ theme }) => theme.layerMargin}px;
	background-color: ${({ theme }) => theme.colors.secondaryDark};
`;

const LayerLabel = styled.div`
    font-family: Open Sans;
    font-style: normal;
    font-weight: 300;
    font-size: 16px;
    padding-left: 15px;
    color: ${({ theme }) => theme.colors.text1};
`;
class Layer extends React.Component {
    getLayerLabel = () => {
        const { layer } = this.props;
        if (layer.isBaseLayer) {
            return `Base layer`;
        }
        if (layer.type === Constants.MEDIA_TYPE.CAPTION) {
            return `Captions`;
        }
        if (layer.type === Constants.MEDIA_TYPE.AUDIO) {
            return `Audio`;
        }
    };

    onLayerClick = () => {
        this.props.ui.clearActiveSection();
        this.props.ui.clearActiveTransition();
    };

    componentDidUpdate() {
        const { layer, isOver } = this.props;
        if (isOver && !layer.isHovered) {
            layer.setIsHovered(true);
        }
        if (!isOver && layer.isHovered) {
            layer.setIsHovered(false);
        }
    }

    render() {
        const { connectDropTarget } = this.props;
        return (
            <LayerContainer
                data-testid={`layer-${this.props.id}`}
                {...this.props}
                onClick={this.onLayerClick}
                ref={connectDropTarget}
            >
                <LayerLabel type={this.props.layer.type}>
                    {this.getLayerLabel()}
                </LayerLabel>
                {this.props.children}
            </LayerContainer>
        );
    }
}

Layer.propTypes = {
    id: PropTypes.string.isRequired,
    ui: PropTypes.object.isRequired,
    layer: PropTypes.object.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    isOver: PropTypes.bool.isRequired,
    canDrop: PropTypes.bool
};

export const LayerComponent = Layer;

export const BoundLayer = CustomDropTarget(
    allowedLayerDragSources,
    spec,
    collect
)(observer(Layer));

export default inject(({ composition, ui }, { id }) => ({
    ui,
    composition,
    layer: composition.getLayer(id)
}))(BoundLayer);
